import { useMemo } from 'react';
import { components, SORT_ORDER } from '@common/types';
import { ColumnDensity } from '@common/types';
import {
  GridColDef,
  GridColumnOrderChangeParams,
  GridColumnResizeParams,
  GridPaginationModel,
  GridPinnedColumnFields,
  GridSortDirection,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import { Box, Chip, IconButton } from '@mui/material';
import { TruncatedTextWithConditionalTooltip } from '@components';
import { ClipboardButton } from 'components/ClipboardButton';
import { getTimeOffStatusColor, TimeOffStatusLabels } from '@util/timeOff';
import { formatDateShort } from '@util/date';
import { capitalize } from 'lodash';
import { t } from 'i18next';
import { buildGridColumns, getReorderedColumns } from '@util/dataGrid';
import { AddTimeOff } from '../components/AddTimeOff';
import DeleteIcon from '@mui/icons-material/Delete';
import { MAX_PINNED_COLUMNS } from '@common/types/constants';

export interface ITimeOffTableFilters {
  sort_by: TimeOffColumnNames;
  sort_order: SORT_ORDER;
  page: number;
  limit: number;
  only_upcoming?: boolean;
  start_date?: string;
  end_date?: string;
  user_ids?: string[];
}

export type TimeOffColumnNames =
  'user' | 'role' | 'start_date' | 'end_date' | 'duration' | 'type' | 'status';

export type ITimeOffTableEntry = components['schemas']['FilteredUserTimeOff'];

export type PersistedTimeOffColumn = Pick<
  GridColDef,
  | 'field'
  | 'headerName'
  | 'width'
  | 'minWidth'
  | 'flex'
  | 'sortable'
  | 'filterable'
  | 'disableColumnMenu'
  | 'align'
  | 'headerAlign'
>;

export const initialTimeOffColumns: PersistedTimeOffColumn[] = [
  {
    field: 'user',
    headerName: 'User',
    minWidth: 220,
    flex: 1,
    sortable: true,
    filterable: false,
  },
  {
    field: 'role',
    headerName: 'Role',
    minWidth: 150,
    flex: 1,
    sortable: true,
    filterable: false,
  },
  {
    field: 'start_date',
    headerName: 'Start date',
    minWidth: 130,
    flex: 1,
    sortable: true,
    filterable: false,
  },
  {
    field: 'end_date',
    headerName: 'End date',
    minWidth: 130,
    flex: 1,
    sortable: true,
    filterable: false,
  },
  {
    field: 'duration',
    headerName: 'Duration',
    minWidth: 110,
    flex: 1,
    sortable: false,
    filterable: false,
  },
  {
    field: 'type',
    headerName: 'Type',
    minWidth: 110,
    flex: 1,
    sortable: false,
    filterable: false,
  },
  {
    field: 'status',
    headerName: 'Status',
    width: 150,
    minWidth: 150,
    sortable: false,
    filterable: false,
    headerAlign: 'center',
  },
  {
    field: 'actions',
    headerName: '',
    width: 100,
    minWidth: 100,
    sortable: false,
    filterable: false,
    disableColumnMenu: true,
  },
];

interface IBuildTimeOffColumnsOptions {
  isDense: boolean;
  disabled: boolean;
  hasEditPermission: boolean;
  onDeleteClick: (id: string) => void;
}

export const buildTimeOffColumns = ({
  isDense,
  disabled,
  hasEditPermission,
  onDeleteClick,
}: IBuildTimeOffColumnsOptions): GridColDef<ITimeOffTableEntry>[] => [
  {
    field: 'user',
    headerName: 'User',
    minWidth: 220,
    flex: 1,
    sortable: true,
    filterable: false,
    renderCell: params => (
      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
        <ClipboardButton textToCopy={`${params.row.user_email}`} size={16} />
        <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <TruncatedTextWithConditionalTooltip
            width='100%'
            variant='body2'
            text={params.row.user_name || ''}
          />
          {!isDense && (
            <TruncatedTextWithConditionalTooltip
              width='100%'
              variant='caption'
              color='textSecondary'
              text={params.row.user_email || ''}
            />
          )}
        </Box>
      </Box>
    ),
  },
  {
    field: 'role',
    headerName: 'Role',
    minWidth: 150,
    flex: 1,
    sortable: true,
    filterable: false,
    renderCell: params => (
      <TruncatedTextWithConditionalTooltip
        width='100%'
        variant='body2'
        text={t(`roles.${params.row.user_role}`) || ''}
      />
    ),
  },
  {
    field: 'start_date',
    headerName: 'Start date',
    minWidth: 130,
    flex: 1,
    sortable: true,
    filterable: false,
    renderCell: params => (
      <TruncatedTextWithConditionalTooltip
        width='100%'
        variant='body2'
        text={formatDateShort(params.row.start_date)}
      />
    ),
  },
  {
    field: 'end_date',
    headerName: 'End date',
    minWidth: 130,
    flex: 1,
    sortable: true,
    filterable: false,
    renderCell: params => (
      <TruncatedTextWithConditionalTooltip
        width='100%'
        variant='body2'
        text={formatDateShort(params.row.end_date)}
      />
    ),
  },
  {
    field: 'duration',
    headerName: 'Duration',
    minWidth: 110,
    flex: 1,
    sortable: false,
    filterable: false,
    renderCell: params => (
      <TruncatedTextWithConditionalTooltip
        width='100%'
        variant='body2'
        text={`${params.row.days} days`}
      />
    ),
  },
  {
    field: 'type',
    headerName: 'Type',
    minWidth: 110,
    flex: 1,
    sortable: false,
    filterable: false,
    renderCell: params => (
      <TruncatedTextWithConditionalTooltip
        width='100%'
        variant='body2'
        text={capitalize(params.row.type)}
      />
    ),
  },
  {
    field: 'status',
    headerName: 'Status',
    width: 150,
    minWidth: 150,
    sortable: false,
    filterable: false,
    headerAlign: 'center',
    renderCell: params => (
      <Chip
        sx={{
          backgroundColor: getTimeOffStatusColor(params.row.status),
          color: theme => theme.palette.common.white,
          mx: 'auto',
          height: '24px',
        }}
        label={TimeOffStatusLabels[params.row.status] || 'Status unknown'}
      />
    ),
  },
  {
    field: 'actions',
    headerName: '',
    width: 100,
    minWidth: 100,
    sortable: false,
    filterable: false,
    disableColumnMenu: true,
    renderCell: params => (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          justifyContent: 'flex-end',
          width: '100%',
        }}
      >
        <AddTimeOff
          variant='edit'
          time_off_id={params.row.id}
          disabled={disabled || !hasEditPermission}
        />
        <IconButton
          disabled={disabled || !hasEditPermission}
          onClick={e => {
            e.stopPropagation();
            onDeleteClick(params.row.id);
          }}
        >
          <DeleteIcon
            color={disabled || !hasEditPermission ? 'disabled' : 'error'}
          />
        </IconButton>
      </Box>
    ),
  },
];

interface IUseTimeOffTableProps {
  filters: ITimeOffTableFilters;
  setFilters: (filters: ITimeOffTableFilters) => void;
  persistedColumns: PersistedTimeOffColumn[];
  setPersistedColumns: (columns: PersistedTimeOffColumn[]) => void;
  pinnedColumns: GridPinnedColumnFields;
  setPinnedColumns: (pinnedColumns: GridPinnedColumnFields) => void;
  hiddenColumns: string[];
  setHiddenColumns: (hiddenColumns: string[]) => void;
  density: ColumnDensity;
  disabled: boolean;
  hasEditPermission: boolean;
  onDeleteClick: (id: string) => void;
}

export const useTimeOffTable = ({
  filters,
  setFilters,
  persistedColumns,
  setPersistedColumns,
  pinnedColumns,
  setPinnedColumns,
  hiddenColumns,
  setHiddenColumns,
  density,
  disabled,
  hasEditPermission,
  onDeleteClick,
}: IUseTimeOffTableProps) => {
  const { sort_by, sort_order } = filters;

  const apiRef = useGridApiRef();

  const handleChangeOrder = (field: string, sort: GridSortDirection) => {
    if (!field || !sort) {
      setFilters({ ...filters, sort_by: 'user', sort_order: 'asc' });
      return;
    }
    setFilters({
      ...filters,
      sort_by: field as TimeOffColumnNames,
      sort_order: sort,
    });
  };

  const onPageModelChange = (model: GridPaginationModel) => {
    setFilters({ ...filters, limit: model.pageSize, page: model.page + 1 });
  };

  const runtimeColumns = useMemo(
    () =>
      buildTimeOffColumns({
        isDense: density === 'dense',
        disabled,
        hasEditPermission,
        onDeleteClick,
      }),
    [density, disabled, hasEditPermission, onDeleteClick],
  );

  const columns = buildGridColumns<ITimeOffTableEntry, PersistedTimeOffColumn>(
    runtimeColumns,
    persistedColumns,
  );

  const toPersistedColumn = (
    col: GridColDef<ITimeOffTableEntry>,
  ): PersistedTimeOffColumn => ({
    field: col.field,
    headerName: col.headerName,
    width: col.width,
    minWidth: col.minWidth,
    flex: col.flex,
    sortable: col.sortable,
    filterable: col.filterable,
    disableColumnMenu: col.disableColumnMenu,
    align: col.align,
    headerAlign: col.headerAlign,
  });

  const handleColumnOrderChange = (_params: GridColumnOrderChangeParams) => {
    const next = getReorderedColumns(apiRef, columns);
    setPersistedColumns(next.map(toPersistedColumn));
  };

  const handleColumnWidthChange = (params: GridColumnResizeParams) => {
    const next = columns.map(col =>
      col.field === params.colDef.field
        ? { ...col, width: params.width, flex: undefined }
        : col,
    );
    setPersistedColumns(next.map(toPersistedColumn));
  };

  const handlePinnedColumnsChange = (model: GridPinnedColumnFields) => {
    setPinnedColumns({
      left: (model.left ?? []).slice(0, MAX_PINNED_COLUMNS),
      right: (model.right ?? []).slice(0, MAX_PINNED_COLUMNS),
    });
  };

  const columnVisibilityModel: Record<string, boolean> = Object.fromEntries(
    columns.map(col => [col.field, !hiddenColumns.includes(col.field)]),
  );

  const handleColumnVisibilityModelChange = (
    model: Record<string, boolean>,
  ) => {
    setHiddenColumns(
      columns.map(col => col.field).filter(field => model[field] === false),
    );
  };

  const MIN_VISIBLE_COLUMNS = 4;
  const visibleColumnsCount = Object.entries(columnVisibilityModel).filter(
    ([field, visible]) => field !== 'actions' && visible,
  ).length;
  const canHideColumn = visibleColumnsCount > MIN_VISIBLE_COLUMNS;
  const columnsWithHideable = columns.map(col => ({
    ...col,
    hideable: canHideColumn,
  }));

  return {
    sort_by,
    sort_order,
    handleChangeOrder,
    onPageModelChange,
    columns: columnsWithHideable,
    handleColumnOrderChange,
    handleColumnWidthChange,
    pinnedColumns,
    handlePinnedColumnsChange,
    columnVisibilityModel,
    handleColumnVisibilityModelChange,
    initialSortFilters: { sort_by: 'user', sort_order: 'asc' },
    apiRef,
  };
};
